import { useCallback, useEffect, useRef, useState } from "react";

interface MediaDeviceInfo {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

interface PermissionStatus {
  camera: PermissionState | "prompt";
  microphone: PermissionState | "prompt";
}

interface UseMediaDevicesReturn {
  stream: MediaStream | null;
  audioDevices: MediaDeviceInfo[];
  videoDevices: MediaDeviceInfo[];
  outputDevices: MediaDeviceInfo[];
  permissions: PermissionStatus;
  audioLevel: number;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  selectedAudioDevice: string | null;
  selectedVideoDevice: string | null;
  requestPermissions: () => Promise<boolean>;
  startStream: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
  stopStream: () => void;
  toggleAudio: () => void;
  toggleVideo: () => Promise<boolean>;
  selectAudioDevice: (deviceId: string) => Promise<void>;
  selectVideoDevice: (deviceId: string) => Promise<void>;
}

export function useMediaDevices(): UseMediaDevicesReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [permissions, setPermissions] = useState<PermissionStatus>({
    camera: "prompt",
    microphone: "prompt",
  });
  const [audioLevel, setAudioLevel] = useState(0);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string | null>(null);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const placeholderCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const placeholderStreamRef = useRef<MediaStream | null>(null);
  const placeholderVideoTrackRef = useRef<MediaStreamTrack | null>(null);

  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  const getOrCreatePlaceholderVideoTrack = useCallback((): MediaStreamTrack | null => {
    const existingTrack = placeholderVideoTrackRef.current;
    if (existingTrack && existingTrack.readyState !== "ended") return existingTrack;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = 16;
      canvas.height = 9;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const captureStream = (canvas as HTMLCanvasElement & { captureStream?: (frameRate?: number) => MediaStream }).captureStream;
      if (!captureStream) return null;

      const canvasStream = captureStream.call(canvas, 5);
      const track = canvasStream.getVideoTracks()[0] || null;
      if (!track) return null;

      track.enabled = false;
      placeholderCanvasRef.current = canvas;
      placeholderStreamRef.current = canvasStream;
      placeholderVideoTrackRef.current = track;
      return track;
    } catch {
      return null;
    }
  }, []);

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audio = devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      const video = devices
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      const output = devices
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      setAudioDevices(audio);
      setVideoDevices(video);
      setOutputDevices(output);
    } catch (error) {
      console.error("Failed to enumerate devices:", error);
    }
  }, []);

  const checkPermissions = useCallback(async () => {
    try {
      const [cameraPermission, microphonePermission] = await Promise.all([
        navigator.permissions.query({ name: "camera" as PermissionName }),
        navigator.permissions.query({ name: "microphone" as PermissionName }),
      ]);

      setPermissions({
        camera: cameraPermission.state,
        microphone: microphonePermission.state,
      });

      cameraPermission.onchange = () => {
        setPermissions((prev) => ({ ...prev, camera: cameraPermission.state }));
        enumerateDevices();
      };

      microphonePermission.onchange = () => {
        setPermissions((prev) => ({ ...prev, microphone: microphonePermission.state }));
        enumerateDevices();
      };
    } catch {
      // Permissions API not supported in all browsers
    }
  }, [enumerateDevices]);

  const setupAudioAnalyser = useCallback((mediaStream: MediaStream) => {
    const audioTrack = mediaStream.getAudioTracks()[0];
    if (!audioTrack) return;

    audioContextRef.current = new AudioContext();
    const source = audioContextRef.current.createMediaStreamSource(mediaStream);
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;
    source.connect(analyserRef.current);

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const normalized = Math.min(100, Math.round((average / 255) * 100));
      setAudioLevel(normalized);

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, []);

  const cleanupAudioAnalyser = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  const requestPermissions = useCallback(async () => {
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      tempStream.getTracks().forEach((track) => track.stop());

      setPermissions({ camera: "granted", microphone: "granted" });
      await enumerateDevices();
      return true;
    } catch {
      return false;
    }
  }, [enumerateDevices]);

  const startStream = useCallback(
    async (constraints?: MediaStreamConstraints) => {
      const defaultConstraints: MediaStreamConstraints = {
        video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
        audio: {
          ...(selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : {}),
          echoCancellation: true, noiseSuppression: true, autoGainControl: true,
        },
      };

      const requestedConstraints = constraints || defaultConstraints;

      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(requestedConstraints);
      } catch (error) {
        const wantsAudio = Boolean(requestedConstraints.audio);
        const wantsVideo = Boolean(requestedConstraints.video);

        if (wantsAudio && wantsVideo) {
          try {
            mediaStream = await navigator.mediaDevices.getUserMedia({
              audio: requestedConstraints.audio,
              video: false,
            });
          } catch (fallbackError) {
            console.error("Failed to start media stream with fallback:", fallbackError);
            throw error;
          }
        } else {
          throw error;
        }
      }

      const wantsVideo = Boolean(requestedConstraints.video);
      if (wantsVideo && mediaStream.getVideoTracks().length === 0) {
        const placeholderTrack = getOrCreatePlaceholderVideoTrack();
        if (placeholderTrack && !mediaStream.getTrackById(placeholderTrack.id)) {
          try {
            mediaStream.addTrack(placeholderTrack);
          } catch (addTrackError) {
            console.warn("Failed to attach placeholder video track:", addTrackError);
          }
        }
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsAudioEnabled(mediaStream.getAudioTracks().some((t) => t.enabled));
      setIsVideoEnabled(mediaStream.getVideoTracks().some((t) => t.enabled));

      setupAudioAnalyser(mediaStream);
      await enumerateDevices();

      return mediaStream;
    },
    [selectedAudioDevice, selectedVideoDevice, setupAudioAnalyser, enumerateDevices, getOrCreatePlaceholderVideoTrack]
  );

  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      streamRef.current = null;
    }
    placeholderVideoTrackRef.current = null;
    placeholderStreamRef.current = null;
    placeholderCanvasRef.current = null;
    cleanupAudioAnalyser();
    setIsAudioEnabled(true);
    setIsVideoEnabled(true);
  }, [stream, cleanupAudioAnalyser]);

  const toggleAudio = useCallback(() => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsAudioEnabled((prev) => !prev);
    }
  }, [stream]);

  const toggleVideo = useCallback(async (): Promise<boolean> => {
    if (!stream) return isVideoEnabled;

    if (isVideoEnabled) {
      const videoTracks = stream.getVideoTracks();
      videoTracks.forEach((track) => {
        try {
          track.enabled = false;
          track.stop();
        } catch {
          // ignore
        }
      });

      // Keep a single (ended) track in the stream so new peer connections can still
      // negotiate a video transceiver without renegotiation.
      videoTracks.slice(1).forEach((track) => {
        stream.removeTrack(track);
      });

      setIsVideoEnabled(false);
      return false;
    }

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!newVideoTrack) {
        newStream.getTracks().forEach((track) => track.stop());
        setIsVideoEnabled(false);
        return false;
      }

      stream.getVideoTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
        stream.removeTrack(track);
      });

      newVideoTrack.enabled = true;
      stream.addTrack(newVideoTrack);
      setIsVideoEnabled(true);
      return true;
    } catch (error) {
      console.error("Failed to toggle camera:", error);
      setIsVideoEnabled(false);
      return false;
    }
  }, [stream, isVideoEnabled, selectedVideoDevice]);

  const selectAudioDevice = useCallback(
    async (deviceId: string) => {
      setSelectedAudioDevice(deviceId);

      if (stream) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.stop();
          stream.removeTrack(audioTrack);
        }

        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });

        const newAudioTrack = newStream.getAudioTracks()[0];
        if (newAudioTrack) {
          newAudioTrack.enabled = isAudioEnabled;
          stream.addTrack(newAudioTrack);
          cleanupAudioAnalyser();
          setupAudioAnalyser(stream);
        }
      }
    },
    [stream, isAudioEnabled, cleanupAudioAnalyser, setupAudioAnalyser]
  );

  const selectVideoDevice = useCallback(
    async (deviceId: string) => {
      setSelectedVideoDevice(deviceId);

      if (stream) {
        if (!isVideoEnabled) return;

        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.stop();
          stream.removeTrack(videoTrack);
        }

        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
        });

        const newVideoTrack = newStream.getVideoTracks()[0];
        if (newVideoTrack) {
          newVideoTrack.enabled = isVideoEnabled;
          stream.addTrack(newVideoTrack);
        }
      }
    },
    [stream, isVideoEnabled]
  );

  useEffect(() => {
    checkPermissions();
    enumerateDevices();

    navigator.mediaDevices.addEventListener("devicechange", enumerateDevices);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", enumerateDevices);
    };
  }, [checkPermissions, enumerateDevices]);

  useEffect(() => {
    return () => {
      const currentStream = streamRef.current;
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      cleanupAudioAnalyser();
    };
  }, [cleanupAudioAnalyser]);

  return {
    stream,
    audioDevices,
    videoDevices,
    outputDevices,
    permissions,
    audioLevel,
    isAudioEnabled,
    isVideoEnabled,
    selectedAudioDevice,
    selectedVideoDevice,
    requestPermissions,
    startStream,
    stopStream,
    toggleAudio,
    toggleVideo,
    selectAudioDevice,
    selectVideoDevice,
  };
}
